/**
 * 客户端图片预处理（Web）。
 *
 * 资源服务（Hq.Res.Api）**不做任何图像处理**：不解码、不压缩、不转码、不生成缩略图，
 * 只在入库时记录客户端上报的宽高。因此体积、尺寸、格式必须在上传前由客户端收敛：
 * - 手机直出照片动辄 3~8MB，原样上传会拖慢弱网上传、放大存储与带宽；
 * - HEIC / BMP 等格式浏览器不便直接展示，统一转成 JPEG/PNG 后再传；
 * - 长边超过 1600 的图片在业务页面里没有意义（列表/抽屉最多显示 ~600）。
 */

export interface PrepareImageOptions {
  /** 长边上限（像素），默认 1600。 */
  maxEdge?: number;
  /** 重新编码质量（仅 JPEG/WebP 生效），默认 0.82。 */
  quality?: number;
  /** 原图体积低于该值且尺寸达标时直接原样上传，避免二次编码损失，默认 1.5MB。 */
  maxBytes?: number;
  /**
   * 强制转 JPEG（头像/证件照类不透明图）：透明像素铺白底。
   * 默认 false —— PNG/WebP 保持原格式，避免 LOGO 透明背景变黑。
   */
  preferJpeg?: boolean;
}

export interface PreparedImage {
  /** 待上传的二进制（已是 File 或 Blob）。 */
  file: Blob;
  /** 上传用文件名（扩展名与实际编码一致）。 */
  fileName: string;
  /** 处理后像素宽高（交给资源服务入库，服务端不解码图片）。 */
  width: number;
  height: number;
  /** 是否发生了重编码（便于调用方提示"已压缩"）。 */
  transcoded: boolean;
}

const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY = 0.82;
const DEFAULT_MAX_BYTES = 1.5 * 1024 * 1024;

/** 浏览器不便直接展示、业务上也没必要原样保存的格式 → 统一转 JPEG。 */
const FORCE_JPEG_EXT = new Set(['heic', 'heif', 'bmp']);

function extOf(name: string): string {
  const index = name.lastIndexOf('.');
  return index < 0 ? '' : name.slice(index + 1).toLowerCase();
}

function baseNameOf(name: string): string {
  const index = name.lastIndexOf('.');
  return index <= 0 ? name : name.slice(0, index);
}

async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return { source: bitmap, width: bitmap.width, height: bitmap.height };
    } catch {
      // HEIC 等在多数浏览器里 createImageBitmap 直接失败，回落到 <img> 再试一次
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('图片无法解码，请改用 JPG / PNG 后重试'));
      img.src = url;
    });
    return { source: image, width: image.naturalWidth, height: image.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * 预处理一张图片：必要时等比缩放并重编码，返回可直接上传的 Blob 与尺寸。
 * 非图片（PDF 等）请勿调用本函数。
 */
export async function prepareImageUpload(
  file: File,
  options: PrepareImageOptions = {}
): Promise<PreparedImage> {
  const maxEdge = options.maxEdge && options.maxEdge > 0 ? options.maxEdge : DEFAULT_MAX_EDGE;
  const quality = options.quality ?? DEFAULT_QUALITY;
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;

  const ext = extOf(file.name);
  const { source, width, height } = await decode(file);
  const longEdge = Math.max(width, height);
  const needResize = longEdge > maxEdge;
  const needShrink = file.size > maxBytes;
  const needJpeg = options.preferJpeg || FORCE_JPEG_EXT.has(ext);

  // 已达标：原样上传（避免无谓的二次编码）
  if (!needResize && !needShrink && !needJpeg && (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'webp')) {
    return {
      file,
      fileName: file.name,
      width,
      height,
      transcoded: false
    };
  }

  const scale = needResize ? maxEdge / longEdge : 1;
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('当前浏览器不支持图片压缩，请升级浏览器后重试');

  // 非 JPEG 输出保留透明；转 JPEG 时铺白底，避免透明区域变黑
  const mime = needJpeg ? 'image/jpeg' : ext === 'webp' ? 'image/webp' : 'image/png';
  if (mime === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }
  ctx.drawImage(source, 0, 0, targetWidth, targetHeight);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((value) => resolve(value), mime, quality)
  );
  if (!blob) throw new Error('图片压缩失败，请重试');

  const nextExt = mime === 'image/jpeg' ? 'jpg' : mime === 'image/webp' ? 'webp' : 'png';
  return {
    file: blob,
    fileName: `${baseNameOf(file.name) || 'image'}.${nextExt}`,
    width: targetWidth,
    height: targetHeight,
    transcoded: true
  };
}
